<script setup>
import Icon from 'vue-elements/icon/Icon.vue';

import PostalBox from 'vue-elements/comparison-inputs/PostalBox.vue';
import sortBy from 'lodash/sortBy';
import { computed, ref, watch } from 'vue';
import SelectInput from 'vue-elements/input/SelectInput.vue';
import LightSwitch from 'vue-elements/input/LightSwitch.vue';
import RadioInput from 'vue-elements/input/RadioInput.vue';
import {
  contractTypesWithoutDynamic,
  extraProviders,
  providers,
  sortModes,
  sustainabilityScores,
} from '../lib/filterOptions';

import ConsumptionInput from './inputs/ConsumptionInput.vue';
import ModalHook from '../../../../abstracts/elements/modal/_shared/hook/modalHook.vue';
import ButtonIconRight from '../../../../abstracts/elements/button/icon-right/button-icon-right.vue';

import { useEnergyComparisonStore } from '../stores/energyComparison';
import CheckboxList from '../../../vue-elements/input/CheckBoxList.vue';
import LinkIconRight from '../../../../abstracts/elements/link/icon-right/link-icon-right.vue';
import { logAnalyticsEvent } from '../../../../assets/js/helpers/bananas.js';
import Tooltip from '../../../../abstracts/elements/tooltip/tooltip.vue';
import FieldLabelWithTooltip from '../../../vue-elements/field/FieldLabelWithTooltip.vue';

const comparisonStore = useEnergyComparisonStore();

const providerList = computed(() => {
  if (comparisonStore.filters.allProviders) {
    const allProviders = [...extraProviders, ...providers];

    return sortBy(allProviders, [
      (item) => item.label.toLowerCase(),
    ]);
  }

  return providers;
});

const props = defineProps({
  // savedComparison: Object,
  savedComparison: null,
  signedIn: Boolean,
  show: Boolean,
});

const emits = defineEmits(['changeAddress']);
const consumptionInputOpen = ref(false);
const consumptionInput = ref(null);

watch(() => props.show, (value) => {
  if (!value && consumptionInputOpen.value) {
    consumptionInput.value.submit();
  }
});

watch(consumptionInputOpen, (value) => {
  if (value) {
    logAnalyticsEvent('compare-energy.change-consumption');
  }
});
watch(() => comparisonStore.tariffType, (value) => {
  if (value === 'dynamic') {
    comparisonStore.filters.contractTypes = ['dynamic'];
  } else {
    comparisonStore.filters.contractTypes = ['12'];
  }
});

</script>

<template>
  <div class="filters" :class="{ expand: show }">
    <div class="innerfilters">
      <transition name="fade" mode="out-in">
        <div v-if="!comparisonStore.loading && !comparisonStore.locationFound || comparisonStore.locationLoading"
          class="panel">
          <postal-box :location="comparisonStore.compareLocation" :loading="comparisonStore.loading"
            @postal="comparisonStore.doPostal($event)" />
        </div>
      </transition>



      <div class="panel">
        <div class="header">
          Jouw verbruik
        </div>

        <div v-show="!consumptionInputOpen" class="consumption-summary">
          <div v-if="!comparisonStore.filters.doubleMeter" class="consumption-item">
            <div class="label">
              <icon icon="x-fa-bolt" size="sm" />
              Stroom
            </div>
            <hr class="line">
            <span class="value" @click="consumptionInputOpen = true">{{ comparisonStore.filters.electricity }}
              kWh</span>
          </div>

          <div v-if="comparisonStore.filters.doubleMeter" class="consumption-item">
            <span class="label">
              <icon icon="x-fa-bolt" size="sm" />
              Stroom normaal
            </span>
            <hr class="line">

            <span class="value" @click="consumptionInputOpen = true">{{ comparisonStore.filters.electricity }}
              kWh</span>
          </div>
          <div v-if="comparisonStore.filters.doubleMeter" class="consumption-item">
            <span class="label">
              <icon icon="x-fa-bolt" size="sm" />
              Stroom dal
            </span>
            <hr class="line">
            <span class="value" @click="consumptionInputOpen = true">{{ comparisonStore.filters.electricityOffPeak }}
              kWh</span>
          </div>

          <div v-if="comparisonStore.filters.electricityGenerated" class="consumption-item">
            <div class="label">
              <icon icon="x-fa-solar-panel" size="sm" />

              Zonnepanelen
            </div>
            <hr class="line">

            <span class="value" @click="consumptionInputOpen = true">{{ comparisonStore.filters.electricityGeneration }}
              kWh</span>
          </div>

          <div v-if="!comparisonStore.filters.noGas" class="consumption-item">
            <div class="label">
              <icon icon="x-fa-flame" size="sm" />
              Gas
            </div>
            <hr class="line">

            <span class="value" @click="consumptionInputOpen = true">{{ comparisonStore.filters.gas }} m³</span>
          </div>
        </div>
        <LinkIconRight v-if="!consumptionInputOpen" label="Verbruik aanpassen" icon="x-fa-edit"
          @click="consumptionInputOpen = true" />
        <consumption-input v-show="consumptionInputOpen" ref="consumptionInput" @close="consumptionInputOpen = false" />
      </div>
      <div v-if="comparisonStore.compareLocation" class="panel">
        <span class="header address">{{ comparisonStore.compareLocation.display }}</span>
        <span class="filter-update-toggle change-address" @click="$emit('changeAddress')">
          <div class="toggle">
            <span class="label">Wijzig</span>
            <icon icon="x-fa-edit" size="sm" />
          </div>
        </span>
      </div>
      <div class="panel">
        <span class="header">Filteren</span>
        <div class="filter-field">

          <FieldLabelWithTooltip label="Type contract" id="contract-types">
            <template #tooltip>
              <Tooltip>
                <template #text>
                  Bij een dynamisch contract veranderen de tarieven mee met de marktprijs. Dit is elk uur voor stroom en
                  elke dag voor gas.
                  <br> <br>
                  <strong>Ons advies</strong>
                  Kies een vast contract als je zekerheid wilt over je tarieven. Vind je het leuk om de markt in de
                  gaten te houden en kan je jouw verbruik daarop aanpassen? Dan is een dynamisch contract misschien wat
                  voor jou.
                </template>
              </Tooltip>
            </template>
          </FieldLabelWithTooltip>

          <div class="radio-list">
            <radio-input id="contract_type_fixed" v-model="comparisonStore.tariffType" type="alt" title="Vast"
              value="fixed" name="contract_type" />
            <radio-input id="contract_type_dynamic" v-model="comparisonStore.tariffType" type="alt" title="Dynamisch"
              value="dynamic" name="contract_type" />
          </div>
        </div>
        <div class="filter-field">

          <FieldLabelWithTooltip label="Contractduur" id="contract-durations-label"
            v-if="comparisonStore.tariffType === 'fixed'">
            <template #tooltip>
              <Tooltip>
                <template #text>
                  <strong>Ons advies</strong>
                  Kies een 1-jarig contract. Zo heb je zekerheid maar ook de mogelijkheid om volgend jaar weer van een
                  welkomstkorting te profiteren.
                  <br> <br>
                  Wil je liever zekerheid voor een langere periode? Kies dan voor een contract van 2 of 3 jaar.
                </template>
              </Tooltip>
            </template>
          </FieldLabelWithTooltip>

          <checkbox-list v-if="comparisonStore.tariffType === 'fixed'" id="contract-types"
            v-model="comparisonStore.filters.contractTypes" :options="contractTypesWithoutDynamic" />
        </div>
        <div class="filter-field">

          <FieldLabelWithTooltip label="Aan te vragen via Keuze.nl">
            <template #tooltip>
              <Tooltip>
                <template #text>
                  Bij deze leveranciers regelen wij de overstap voor je. Je hoeft dan niet je gegevens opnieuw in te
                  vullen bij de leverancier. Zo zorgen wij ervoor dat je snel en zorgeloos kunt overstappen.
                  <br> <br>
                  <strong>Overstap herinnering</strong>
                  Wanneer je contract bijna afloopt, sturen wij je een herinnering. Zo weet je zeker dat je nooit te
                  veel
                  betaalt.
                </template>
              </Tooltip>
            </template>
          </FieldLabelWithTooltip>
          <LightSwitch id="only-direct-orders" v-model="comparisonStore.filters.onlyDirectOrders" />
        </div>

        <div class="filter-field">
          <FieldLabelWithTooltip label="Duurzaamheid" id="sustainability-scores">
            <template #tooltip>
              <Tooltip>
                <template #text>
                  De Groenscore geeft aan hoe de leverancier scoort op het gebied van duurzaamheid, waarbij A de hoogste
                  en C de laagste score is.
                  <br> <br>
                  <strong>Ons advies</strong>
                  Vind je duurzaamheid belangrijk? Kies dan voor een leverancier met een hoge Groenscore. Ook
                  leveranciers met een lagere score kunnen duurzaam zijn. Kijk daarom ook naar de toelichting van de
                  score.
                </template>
              </Tooltip>
            </template>
          </FieldLabelWithTooltip>
          <SelectInput id="sustainability-scores" v-model="comparisonStore.filters.minSustainability"
            :options="sustainabilityScores" type="button" />
        </div>
      </div>

      <!-- <div class="panel">
        <span class="header">Sorteren </span>
        <div class="field">
          <SelectInput id="sort-modes" v-model="comparisonStore.filters.sort" label="Sorteer op" :options="sortModes"
            type="button" />
        </div>
        <div class="field">
          <LightSwitch id="show-price-per-year" v-model="comparisonStore.filters.pricePerYear"
            label="Toon prijzen per jaar" />
        </div>
      </div> -->
      <transition name="fade" mode="out-in">
        <div v-if="comparisonStore.products.length > 0 && !!comparisonStore.compareLocation"
          class="panel save-comparison-component desktop" @click="$emit('hideFilters')">
          <modal-hook :name="'save-comparison'">
            <template #content>
              <button-icon-right type="ghost" label="Mail mij deze vergelijking" icon="x-fa-envelope"
                :disabled="!!savedComparison" />
            </template>
          </modal-hook>
          <div v-if="savedComparison" class="bubble">
            <icon icon="check-circle-solid" size="sm" />
            <span class="text">{{ signedIn ? 'Deze vergelijking is bewaard' : 'Deze vergelijking is gemaild' }}</span>
          </div>
        </div>
      </transition>
      <div class="panel">
        <span class="header">Leveranciers</span>
        <checkbox-list v-model="comparisonStore.filters.providers" :options="providerList" />
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@use "assets/scss/_helpers/typography";
@use "assets/scss/_helpers/display";
@use "assets/scss/_helpers/breakpoint";
@use "assets/scss/_variables/colors";
@use 'abstracts_depr/elements/checkbox/checkbox';
@use 'abstracts/elements/link/icon-right/link-icon-right';
@import "templates/elements/link/button/primary/link.scss";

.filters {
  color: colors.$text-base;
  z-index: 10;

  .radio-list {
    display: grid;
    grid-gap: 8px;
    margin-bottom: 16px;
  }

  .header {
    @include typography.font((24px, 150%, 700));
    display: block;
    font-weight: bold;
    margin-bottom: 12px;

    &.address {
      font-size: 18px;
    }
  }

  .filter-field {
    margin-bottom: 24px;
  }

  .tooltip.top-right .tooltip-content-container {
    @include breakpoint.down(sm) {
      left: -8px !important; // Negate the padding of the filterbar
    }
  }

  // .button.primary {
  //   @extend %button;
  //   @extend %button--primary;

  //   border-radius: radius("button");
  //   border-color: transparent;

  //   &:hover {
  //     color: colors.$text-light;
  //     box-shadow: 0px 0 0 rgb(0 0 0 / 5%),
  //       inset 0px 3px 3px 1px rgb(177 130 86 / 39%);
  //     border-color: transparent;
  //   }
  // }

  .filter-update-toggle {
    display: flex;
    margin-top: 12px;
    cursor: pointer;
    text-decoration: none;

    .toggle {
      @include link-icon-right.border-bottom();
    }

    .icon-element {
      margin-left: 12px;
      background: colors.$link-base-default;
    }
  }
}

.comparison-module .comparison-body .innerfilters {
  box-shadow: 0 0 25px rgba(0, 0, 0, 0.15);
  border-radius: 16px;
  background: colors.$block-base;

  .consumption-summary {
    margin-bottom: 12px;
  }

  .consumption-item {
    @include display.flex-row-between;
    @include typography.font((16px, 160%, 400));

    .value {
      cursor: pointer;
    }

    +.consumption-item {
      margin-top: 8px;
    }

    hr.line {
      height: 1px;
      flex: 1;
      align-items: center;
      justify-content: center;
      margin: 0 12px;
      color: colors.$text-subtle;
    }
  }

  .panel {
    padding: 32px;
    border-bottom: 1px solid colors.$border-base;

    &:last-child {
      border-bottom: 0;
    }
  }

  .field-label>.label {
    margin-bottom: 8px;
    font-weight: 500;
  }

  .speed-advice {
    .button {
      height: 48px;
      display: flex;
      padding: 0 12px;
      border-radius: 16px;
      border: 2px solid colors.$link-base-default;
      margin: 8px 0;

      .icon-element {
        background-color: colors.$link-base-default;
        width: 24px;
        height: 24px;
      }

      &>span {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        color: colors.$link-base-default;
      }
    }
  }

  .label.label-combi {
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    &>div,
    label {
      display: inline-flex;
    }
  }

  .label-value {
    font-weight: 700;
  }


  .label-with-tooltip {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
    position: relative;

    .field-label {
      margin-bottom: 0;
    }
  }

  .field+.field {
    margin-top: 12px;
  }
}

.theme-energy-price-focus .filters {
  .header {
    font-weight: 600;
  }

  .innerfilters {
    background: transparent;
    box-shadow: none;

    .panel {
      padding: 24px;
      border-bottom: 1px solid #d4e5ec;
      border-right: 1px solid #d4e5ec;

      @include breakpoint.up(lg) {
        padding-left: 0;
        padding-right: 48px;
        padding-bottom: 32px;

        &:first-of-type {
          padding-top: 0;
        }
      }
    }
  }
}
</style>
